/**
 * NEON RAPIDS — instance.
 *
 * A vertical-scroll fuel-gauge river shooter. The ship flies UP a winding
 * neon canyon (the world scrolls downward on screen). Steer left/right
 * across the river; the canyon walls are lethal. Fire forward to kill
 * patrol boats, helicopters, and jets for points.
 *
 * The signature mechanic is FUEL: the tank drains constantly, so every
 * run is a race against empty. Fly OVER a green fuel depot to refill —
 * but shooting a depot destroys it (and its fuel), the classic risk/
 * reward. Bridges span the river at intervals: they're score gates AND
 * checkpoints — pass one and you bank a respawn point. 3 lives; a crash
 * (wall, enemy, or dry tank) costs one and respawns you at the last
 * bridge with a partial tank. Lose all three → game over.
 *
 * Endless: the river scrolls forever and ramps up (faster scroll, denser
 * and faster enemies, tighter channel) with distance. Score = kills +
 * distance + bridge bonuses. Deterministic: all randomness via
 * ctx.random so the server validator can replay a run byte-for-byte.
 *
 * KEEP IN SYNC with the remaster (apps/games/neon-rapids/src/main.js) —
 * same fuel economy, lives, and scoring intent.
 */

import type {
  GameContext,
  GameInstance,
  InputState,
  LoopHandle,
} from '@arcraz/arcadia-types';
import {
  ATTRACT_PROMPT,
  ATTRACT_TITLE,
  BOOM_CORE,
  BOOM_HOT,
  BOOM_MID,
  BRIDGE_BODY,
  BRIDGE_DARK,
  BRIDGE_LIGHT,
  BULLET,
  BULLET_GLOW,
  CARD_BG,
  CARD_BORDER,
  CARD_BORDER_OK,
  CARD_SUB,
  CARD_TEXT,
  CRASH_FLASH,
  ENEMY_HELI,
  ENEMY_HELI_DARK,
  ENEMY_JET,
  ENEMY_JET_DARK,
  ENEMY_SHIP,
  ENEMY_SHIP_DARK,
  FUEL_BODY,
  FUEL_CRIT,
  FUEL_DARK,
  FUEL_FRAME,
  FUEL_FULL,
  FUEL_MARK,
  FUEL_WARN,
  HUD_DIST,
  HUD_LABEL,
  HUD_LIVES,
  HUD_SCORE,
  SHIP_BODY,
  SHIP_DARK,
  SHIP_GLOW,
  SHIP_THRUST,
  WALL_BASE,
  WALL_EDGE,
  WALL_TEXTURE,
  WATER_BOTTOM,
  WATER_SHIMMER,
  WATER_TOP,
} from './palette.js';

const WIDTH = 480;
const HEIGHT = 270;

// ---- Ship --------------------------------------------------------------------
const SHIP_Y = HEIGHT - 44; // fixed near the bottom; the world scrolls to it
const SHIP_HALF_W = 7;
const SHIP_SPEED = 150; // px/s lateral steering
const START_LIVES = 3;

// ---- Scroll / distance -------------------------------------------------------
// World scrolls downward; distance is "meters" up-river.
const BASE_SCROLL = 90; // px/s at the start
const SCROLL_RAMP = 46; // extra px/s at full difficulty
const RAMP_DIST = 4000; // meters to reach full difficulty
const PX_PER_M = 1; // 1 scrolled px == 1 meter

// ---- Fuel (the signature mechanic) ------------------------------------------
// KEEP IN SYNC with the remaster.
const FUEL_MAX = 100;
const FUEL_DRAIN_PER_S = 7.0; // ~14s per full tank of pure flying
const FUEL_REFILL_PER_S = 55; // flying over a depot tops up fast
const FUEL_RESPAWN = 60; // partial tank on respawn at a bridge

// ---- Fire --------------------------------------------------------------------
const FIRE_COOLDOWN_TICKS = 9; // ~6.7 shots/s
const BULLET_SPEED = 320; // px/s up-river

// ---- River channel -----------------------------------------------------------
// The river is described by a centerline + half-width per scanline of world.
// We generate it in world-space segments and sample it each frame.
const RIVER_MIN_HALF = 46;
const RIVER_MAX_HALF = 120;
const SEG_LEN_M = 40; // world meters per river control segment

// ---- Spawning ----------------------------------------------------------------
const ENEMY_GAP_MIN_M = 70;
const FUEL_GAP_M = 260; // a depot roughly every this-many meters
const BRIDGE_GAP_M = 900; // a bridge (checkpoint) every this-many meters

// ---- Crash -------------------------------------------------------------------
const CRASH_SETTLE_TICKS = 40;
const RESPAWN_INVULN_TICKS = 70;

type Phase = 'ready' | 'flying' | 'crashed' | 'game-over';
type EnemyKind = 'ship' | 'heli' | 'jet';

interface Enemy {
  kind: EnemyKind;
  /** World distance (meters up-river) of the enemy's center. */
  worldM: number;
  /** Lateral position in px, river-relative absolute screen x when on-screen. */
  x: number;
  vx: number; // lateral drift px/s
  wHalf: number;
  alive: boolean;
  scored: boolean;
}

interface Depot {
  worldM: number;
  x: number;
  alive: boolean; // shot depots are destroyed (no refill)
}

interface Bridge {
  worldM: number;
  passed: boolean;
}

interface Bullet {
  x: number;
  y: number;
  alive: boolean;
}

interface Boom {
  x: number;
  y: number;
  t: number; // 0..1 life
}

interface RiverSeg {
  /** World meters at this control point. */
  m: number;
  center: number; // px
  half: number; // px
}

const KILL_SCORE: Record<EnemyKind, number> = { ship: 60, heli: 100, jet: 150 };
const BRIDGE_SCORE = 500;

export class NeonRapidsInstance implements GameInstance {
  private readonly ctx: GameContext;

  private phase: Phase = 'ready';
  private tick = 0;
  private phaseEnteredTick = 0;

  private distanceM = 0;
  private score = 0;
  private finalScore = 0;
  private lives = START_LIVES;
  private fuel = FUEL_MAX;

  private shipX = WIDTH / 2;
  private lastFireTick = -FIRE_COOLDOWN_TICKS;
  private invulnUntil = 0;

  private river: RiverSeg[] = [];
  private enemies: Enemy[] = [];
  private depots: Depot[] = [];
  private bridges: Bridge[] = [];
  private bullets: Bullet[] = [];
  private booms: Boom[] = [];

  private lastEnemyM = 0;
  private lastDepotM = 0;
  private lastBridgeM = 0;
  private lastCheckpointM = 0;

  private crashFlash = 0;
  private shake = 0;
  private restartArmed = false;

  private inLeft = false;
  private inRight = false;
  private inFire = false;

  private engine: LoopHandle | null = null;

  constructor(ctx: GameContext) {
    this.ctx = ctx;
    void this.ctx.random.next();
  }

  // --------------------------------------------------------------------------
  // GameInstance contract
  // --------------------------------------------------------------------------

  input(state: InputState): void {
    if (this.phase === 'ready') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.startRun();
      }
      return;
    }
    if (this.phase === 'game-over') {
      if (state.justPressed.has('fireA') || state.justPressed.has('start')) {
        this.restartArmed = true;
      }
      return;
    }
    this.inLeft = state.left || state.stickL.x < -0.3;
    this.inRight = state.right || state.stickL.x > 0.3;
    this.inFire = state.fireA || state.fireB || state.justPressed.has('fireA');
  }

  update(_dt: number): void {
    const dt = 1 / 60;
    this.tick++;
    if (this.crashFlash > 0) this.crashFlash = Math.max(0, this.crashFlash - dt * 3);
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 4);
    this.tickBooms(dt);

    if (this.phase === 'ready') return;

    if (this.phase === 'crashed') {
      this.engine?.setGain(0);
      if (this.tick - this.phaseEnteredTick >= CRASH_SETTLE_TICKS) {
        if (this.lives > 0) this.respawn();
        else {
          this.enterPhase('game-over');
          this.finalScore = Math.floor(this.score);
          this.ctx.events.emit('death', { remainingLives: 0 });
        }
      }
      return;
    }
    if (this.phase !== 'flying') return;

    // Advance the world.
    const scroll = this.scrollSpeed();
    this.distanceM += scroll * dt * PX_PER_M;
    this.score += scroll * dt * 0.05; // small distance trickle
    this.ensureRiver();

    // Fuel bleeds every frame; empty tank is fatal.
    this.fuel -= FUEL_DRAIN_PER_S * dt;
    if (this.fuel <= 0) {
      this.fuel = 0;
      this.crash('fuel');
      return;
    }

    this.tickShip(dt);
    this.tickFire();
    this.tickBullets(dt);
    this.tickSpawns();
    this.tickEnemies(dt);
    this.tickDepots();
    this.tickBridges();
    this.tickCollisions();
    this.tickEngineAudio();
  }

  render(g: CanvasRenderingContext2D): void {
    let restore = false;
    if (this.shake > 0) {
      const m = this.shake * 3;
      g.save();
      g.translate(Math.round(Math.sin(this.tick * 12.9) * m), Math.round(Math.cos(this.tick * 9.3) * m));
      restore = true;
    }

    this.drawRiver(g);
    this.drawDepots(g);
    this.drawBridges(g);
    this.drawEnemies(g);
    this.drawBullets(g);
    this.drawBooms(g);
    if (this.phase !== 'crashed' || Math.floor(this.tick / 4) % 2 === 0) this.drawShip(g);

    if (restore) g.restore();

    if (this.crashFlash > 0) {
      g.globalAlpha = Math.min(0.5, this.crashFlash);
      g.fillStyle = CRASH_FLASH;
      g.fillRect(0, 0, WIDTH, HEIGHT);
      g.globalAlpha = 1;
    }

    this.drawHud(g);

    if (this.phase === 'ready') this.drawReadyCard(g);
    else if (this.phase === 'game-over') this.drawGameOverCard(g);
  }

  resize(_w: number, _h: number): void {}
  pause(): void {
    this.engine?.setGain(0);
  }
  resume(): void {}
  teardown(): void {
    this.engine?.stop();
    this.engine = null;
    this.enemies = [];
    this.bullets = [];
    this.depots = [];
    this.bridges = [];
    this.booms = [];
    this.river = [];
  }

  getScore(): number {
    return Math.floor(this.score);
  }
  getFinalScore(): number {
    return this.phase === 'game-over' ? this.finalScore : Math.floor(this.score);
  }
  shouldRestart(): boolean {
    return this.phase === 'game-over' && this.restartArmed;
  }
  isGameOver(): boolean {
    return this.phase === 'game-over';
  }

  // --------------------------------------------------------------------------
  // Simulation
  // --------------------------------------------------------------------------

  private startRun(): void {
    this.phase = 'flying';
    this.phaseEnteredTick = this.tick;
    this.distanceM = 0;
    this.score = 0;
    this.lives = START_LIVES;
    this.fuel = FUEL_MAX;
    this.shipX = WIDTH / 2;
    this.enemies = [];
    this.depots = [];
    this.bridges = [];
    this.bullets = [];
    this.booms = [];
    this.river = [];
    this.lastEnemyM = 0;
    this.lastDepotM = 0;
    this.lastBridgeM = 0;
    this.lastCheckpointM = 0;
    this.invulnUntil = this.tick + RESPAWN_INVULN_TICKS;
    this.ensureRiver();
    if (!this.engine) {
      this.engine = this.ctx.audio.loop({ wave: 'triangle', f: 70, gain: 0, fm: { ratio: 1.0, index: 6 } });
    }
  }

  private respawn(): void {
    this.phase = 'flying';
    this.phaseEnteredTick = this.tick;
    // Rewind to the last checkpoint bridge (or a bit back if none yet).
    this.distanceM = this.lastCheckpointM;
    this.fuel = FUEL_RESPAWN;
    this.shipX = WIDTH / 2;
    this.enemies = [];
    this.bullets = [];
    this.booms = [];
    this.river = [];
    this.lastEnemyM = this.distanceM;
    this.lastDepotM = this.distanceM;
    this.invulnUntil = this.tick + RESPAWN_INVULN_TICKS;
    this.ensureRiver();
  }

  private difficulty(): number {
    return Math.min(1, this.distanceM / RAMP_DIST);
  }
  private scrollSpeed(): number {
    return BASE_SCROLL + SCROLL_RAMP * this.difficulty();
  }

  /** Screen Y (top=far up-river) for a given world distance ahead of the ship. */
  private screenYForWorld(worldM: number): number {
    // The ship sits at SHIP_Y and represents this.distanceM. Larger worldM is
    // further up-river → higher on screen (smaller y).
    return SHIP_Y - (worldM - this.distanceM) * PX_PER_M;
  }

  // ---- River generation -----------------------------------------------------
  private ensureRiver(): void {
    // Keep control points covering from a bit behind the ship to above the top.
    const topM = this.distanceM + SHIP_Y + 80;
    const botM = this.distanceM - 60;
    if (this.river.length === 0) {
      // Seed a straight-ish opening.
      let m = Math.floor(botM / SEG_LEN_M) * SEG_LEN_M;
      let center = WIDTH / 2;
      let half = RIVER_MAX_HALF * 0.8;
      while (m < topM + SEG_LEN_M) {
        this.river.push({ m, center, half });
        const nx = this.nextRiver(center, half);
        center = nx.center;
        half = nx.half;
        m += SEG_LEN_M;
      }
    } else {
      // Extend upward as needed.
      let last = this.river[this.river.length - 1]!;
      while (last.m < topM + SEG_LEN_M) {
        const nx = this.nextRiver(last.center, last.half);
        this.river.push({ m: last.m + SEG_LEN_M, center: nx.center, half: nx.half });
        last = this.river[this.river.length - 1]!;
      }
    }
    // Cull points fully behind.
    this.river = this.river.filter((s) => s.m >= botM - SEG_LEN_M);
  }

  private nextRiver(center: number, half: number): { center: number; half: number } {
    const r = this.ctx.random;
    // Tighten the channel as difficulty climbs.
    const maxHalf = RIVER_MAX_HALF - (RIVER_MAX_HALF - RIVER_MIN_HALF) * 0.5 * this.difficulty();
    const nHalf = Math.max(RIVER_MIN_HALF, Math.min(maxHalf, half + (r.next() - 0.5) * 26));
    // Meander the centerline but keep the banks on-screen.
    let nCenter = center + (r.next() - 0.5) * 48;
    nCenter = Math.max(nHalf + 8, Math.min(WIDTH - nHalf - 8, nCenter));
    return { center: nCenter, half: nHalf };
  }

  /** Sample the river (center, half) at a world distance via linear interp. */
  private sampleRiver(worldM: number): { center: number; half: number } {
    const segs = this.river;
    if (segs.length === 0) return { center: WIDTH / 2, half: RIVER_MAX_HALF };
    if (worldM <= segs[0]!.m) return { center: segs[0]!.center, half: segs[0]!.half };
    const last = segs[segs.length - 1]!;
    if (worldM >= last.m) return { center: last.center, half: last.half };
    for (let i = 0; i < segs.length - 1; i++) {
      const a = segs[i]!;
      const b = segs[i + 1]!;
      if (worldM >= a.m && worldM <= b.m) {
        const t = (worldM - a.m) / (b.m - a.m);
        return { center: a.center + (b.center - a.center) * t, half: a.half + (b.half - a.half) * t };
      }
    }
    return { center: last.center, half: last.half };
  }

  private tickShip(dt: number): void {
    if (this.inLeft) this.shipX -= SHIP_SPEED * dt;
    if (this.inRight) this.shipX += SHIP_SPEED * dt;
    this.shipX = Math.max(SHIP_HALF_W, Math.min(WIDTH - SHIP_HALF_W, this.shipX));
  }

  private tickFire(): void {
    if (!this.inFire) return;
    if (this.tick - this.lastFireTick < FIRE_COOLDOWN_TICKS) return;
    this.lastFireTick = this.tick;
    this.bullets.push({ x: this.shipX, y: SHIP_Y - 10, alive: true });
    this.ctx.audio.sfx('shoot');
  }

  private tickBullets(dt: number): void {
    for (const b of this.bullets) {
      b.y -= BULLET_SPEED * dt;
      if (b.y < -8) b.alive = false;
    }
    this.bullets = this.bullets.filter((b) => b.alive);
  }

  private tickSpawns(): void {
    const r = this.ctx.random;
    const spawnM = this.distanceM + SHIP_Y + 40; // just above the top edge
    const diff = this.difficulty();

    // Enemies.
    const gap = ENEMY_GAP_MIN_M * (1 - diff * 0.45) + 20;
    if (spawnM - this.lastEnemyM >= gap) {
      this.lastEnemyM = spawnM;
      const roll = r.next();
      let kind: EnemyKind;
      if (roll < 0.5) kind = 'ship';
      else if (roll < 0.8) kind = 'heli';
      else kind = 'jet';
      const { center, half } = this.sampleRiver(spawnM);
      const x = center + (r.next() - 0.5) * (half * 1.4);
      const drift = kind === 'ship' ? 0 : (r.next() < 0.5 ? -1 : 1) * (18 + diff * 30);
      this.enemies.push({
        kind,
        worldM: spawnM,
        x: Math.max(8, Math.min(WIDTH - 8, x)),
        vx: drift,
        wHalf: kind === 'jet' ? 7 : 9,
        alive: true,
        scored: false,
      });
    }

    // Fuel depots.
    if (spawnM - this.lastDepotM >= FUEL_GAP_M) {
      this.lastDepotM = spawnM;
      const { center, half } = this.sampleRiver(spawnM);
      const x = center + (r.next() - 0.5) * (half * 0.9);
      this.depots.push({ worldM: spawnM, x: Math.max(10, Math.min(WIDTH - 10, x)), alive: true });
    }

    // Bridges (checkpoints).
    if (spawnM - this.lastBridgeM >= BRIDGE_GAP_M) {
      this.lastBridgeM = spawnM;
      this.bridges.push({ worldM: spawnM, passed: false });
    }
  }

  private tickEnemies(dt: number): void {
    for (const e of this.enemies) {
      if (!e.alive) continue;
      e.x += e.vx * dt;
      const { center, half } = this.sampleRiver(e.worldM);
      // Bounce enemies off the banks so they stay on the water.
      if (e.x < center - half + 6) { e.x = center - half + 6; e.vx = Math.abs(e.vx); }
      if (e.x > center + half - 6) { e.x = center + half - 6; e.vx = -Math.abs(e.vx); }
    }
    // Cull enemies scrolled past the bottom.
    const cullM = this.distanceM - 30;
    this.enemies = this.enemies.filter((e) => e.alive && e.worldM > cullM);
  }

  private tickDepots(): void {
    const cullM = this.distanceM - 30;
    this.depots = this.depots.filter((d) => d.alive && d.worldM > cullM);
  }

  private tickBridges(): void {
    for (const br of this.bridges) {
      if (!br.passed && br.worldM <= this.distanceM) {
        br.passed = true;
        this.score += BRIDGE_SCORE;
        this.lastCheckpointM = br.worldM; // respawn point
        this.ctx.audio.sfx('bridge');
        this.ctx.events.emit('score', { score: Math.floor(this.score), delta: BRIDGE_SCORE });
      }
    }
    const cullM = this.distanceM - 30;
    this.bridges = this.bridges.filter((br) => br.worldM > cullM);
  }

  private tickCollisions(): void {
    const invuln = this.tick < this.invulnUntil;

    // Bullets vs enemies + depots.
    for (const b of this.bullets) {
      if (!b.alive) continue;
      const bulletM = this.distanceM + (SHIP_Y - b.y);
      for (const e of this.enemies) {
        if (!e.alive) continue;
        if (Math.abs(e.worldM - bulletM) < 8 && Math.abs(e.x - b.x) < e.wHalf + 2) {
          e.alive = false;
          b.alive = false;
          this.score += KILL_SCORE[e.kind];
          this.spawnBoom(e.x, this.screenYForWorld(e.worldM));
          this.ctx.audio.sfx('boom');
          this.ctx.events.emit('score', { score: Math.floor(this.score), delta: KILL_SCORE[e.kind] });
          break;
        }
      }
      if (!b.alive) continue;
      // Shooting a fuel depot destroys it (you lose the refill).
      for (const d of this.depots) {
        if (!d.alive) continue;
        if (Math.abs(d.worldM - bulletM) < 9 && Math.abs(d.x - b.x) < 10) {
          d.alive = false;
          b.alive = false;
          this.spawnBoom(d.x, this.screenYForWorld(d.worldM));
          this.ctx.audio.sfx('boom');
          break;
        }
      }
    }
    this.bullets = this.bullets.filter((b) => b.alive);

    if (invuln) return;

    // Ship vs canyon walls.
    const { center, half } = this.sampleRiver(this.distanceM);
    if (this.shipX < center - half + SHIP_HALF_W || this.shipX > center + half - SHIP_HALF_W) {
      this.crash('wall');
      return;
    }

    // Ship vs enemies (fly into one = death).
    for (const e of this.enemies) {
      if (!e.alive) continue;
      if (Math.abs(e.worldM - this.distanceM) < 10 && Math.abs(e.x - this.shipX) < e.wHalf + SHIP_HALF_W) {
        this.crash('enemy');
        return;
      }
    }

    // Ship over a fuel depot → refill (don't consume/destroy it visually until
    // it scrolls past; a depot can top you off across several frames).
    for (const d of this.depots) {
      if (!d.alive) continue;
      if (Math.abs(d.worldM - this.distanceM) < 12 && Math.abs(d.x - this.shipX) < 12) {
        this.fuel = Math.min(FUEL_MAX, this.fuel + FUEL_REFILL_PER_S * (1 / 60));
      }
    }
  }

  private crash(_cause: 'wall' | 'enemy' | 'fuel'): void {
    this.lives -= 1;
    this.enterPhase('crashed');
    this.crashFlash = 0.5;
    this.shake = 1;
    this.spawnBoom(this.shipX, SHIP_Y);
    this.ctx.audio.sfx('crash');
  }

  private enterPhase(p: Phase): void {
    this.phase = p;
    this.phaseEnteredTick = this.tick;
  }

  private spawnBoom(x: number, y: number): void {
    this.booms.push({ x, y, t: 0 });
  }
  private tickBooms(dt: number): void {
    for (const b of this.booms) b.t += dt * 2.4;
    this.booms = this.booms.filter((b) => b.t < 1);
  }

  private tickEngineAudio(): void {
    if (!this.engine) return;
    const frac = this.scrollSpeed() / (BASE_SCROLL + SCROLL_RAMP);
    this.engine.setFrequency(60 + 60 * frac);
    this.engine.setGain(0.08 + 0.05 * frac);
  }

  // --------------------------------------------------------------------------
  // Rendering
  // --------------------------------------------------------------------------

  private drawRiver(g: CanvasRenderingContext2D): void {
    // Canyon base fills everything; the river is a lit teal ribbon on top.
    g.fillStyle = WALL_BASE;
    g.fillRect(0, 0, WIDTH, HEIGHT);

    // Horizontal rock striations for texture.
    g.fillStyle = WALL_TEXTURE;
    const stripeOff = Math.floor(this.distanceM) % 18;
    for (let y = -stripeOff; y < HEIGHT; y += 18) {
      g.fillRect(0, y, WIDTH, 2);
    }

    // Build the river polygon by sampling per scanline.
    const water = g.createLinearGradient(0, 0, 0, HEIGHT);
    water.addColorStop(0, WATER_TOP);
    water.addColorStop(1, WATER_BOTTOM);
    g.fillStyle = water;
    g.beginPath();
    let first = true;
    // Left bank top→bottom.
    for (let y = 0; y <= HEIGHT; y += 4) {
      const worldM = this.distanceM + (SHIP_Y - y);
      const { center, half } = this.sampleRiver(worldM);
      const lx = center - half;
      if (first) { g.moveTo(lx, y); first = false; } else g.lineTo(lx, y);
    }
    // Right bank bottom→top.
    for (let y = HEIGHT; y >= 0; y -= 4) {
      const worldM = this.distanceM + (SHIP_Y - y);
      const { center, half } = this.sampleRiver(worldM);
      g.lineTo(center + half, y);
    }
    g.closePath();
    g.fill();

    // Glowing banks + surface shimmer.
    g.lineWidth = 2;
    for (const side of [-1, 1]) {
      g.strokeStyle = WALL_EDGE;
      g.beginPath();
      let f = true;
      for (let y = 0; y <= HEIGHT; y += 4) {
        const worldM = this.distanceM + (SHIP_Y - y);
        const { center, half } = this.sampleRiver(worldM);
        const x = center + side * half;
        if (f) { g.moveTo(x, y); f = false; } else g.lineTo(x, y);
      }
      g.stroke();
    }
    // A couple of scrolling shimmer dashes down the channel center.
    g.strokeStyle = WATER_SHIMMER;
    g.globalAlpha = 0.4;
    const shOff = Math.floor(this.distanceM * 1.5) % 26;
    for (let y = -shOff; y < HEIGHT; y += 26) {
      const worldM = this.distanceM + (SHIP_Y - y);
      const { center } = this.sampleRiver(worldM);
      g.beginPath();
      g.moveTo(center, y);
      g.lineTo(center, y + 10);
      g.stroke();
    }
    g.globalAlpha = 1;
  }

  private drawDepots(g: CanvasRenderingContext2D): void {
    for (const d of this.depots) {
      if (!d.alive) continue;
      const y = this.screenYForWorld(d.worldM);
      if (y < -14 || y > HEIGHT + 14) continue;
      g.fillStyle = FUEL_DARK;
      g.fillRect(d.x - 9, y - 7, 18, 14);
      g.fillStyle = FUEL_BODY;
      g.fillRect(d.x - 7, y - 5, 14, 10);
      g.fillStyle = FUEL_MARK;
      g.font = 'bold 9px ui-monospace, monospace';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText('F', d.x, y + 1);
      g.textAlign = 'left';
      g.textBaseline = 'top';
    }
  }

  private drawBridges(g: CanvasRenderingContext2D): void {
    for (const br of this.bridges) {
      const y = this.screenYForWorld(br.worldM);
      if (y < -10 || y > HEIGHT + 10) continue;
      g.fillStyle = BRIDGE_DARK;
      g.fillRect(0, y - 6, WIDTH, 12);
      g.fillStyle = BRIDGE_BODY;
      g.fillRect(0, y - 4, WIDTH, 8);
      g.fillStyle = BRIDGE_LIGHT;
      for (let x = 8; x < WIDTH; x += 26) g.fillRect(x, y - 1, 3, 2);
    }
  }

  private drawEnemies(g: CanvasRenderingContext2D): void {
    for (const e of this.enemies) {
      if (!e.alive) continue;
      const y = this.screenYForWorld(e.worldM);
      if (y < -14 || y > HEIGHT + 14) continue;
      if (e.kind === 'ship') {
        g.fillStyle = ENEMY_SHIP_DARK;
        g.fillRect(e.x - 9, y - 4, 18, 8);
        g.fillStyle = ENEMY_SHIP;
        g.fillRect(e.x - 7, y - 3, 14, 5);
        g.fillRect(e.x - 2, y - 6, 4, 4);
      } else if (e.kind === 'heli') {
        g.fillStyle = ENEMY_HELI_DARK;
        g.fillRect(e.x - 8, y - 3, 16, 6);
        g.fillStyle = ENEMY_HELI;
        g.fillRect(e.x - 6, y - 2, 12, 4);
        // rotor
        g.fillStyle = ENEMY_HELI_DARK;
        const w = Math.floor(this.tick / 2) % 2 === 0 ? 14 : 4;
        g.fillRect(e.x - w / 2, y - 5, w, 1);
      } else {
        // jet — a fast arrowhead
        g.fillStyle = ENEMY_JET_DARK;
        g.beginPath();
        g.moveTo(e.x, y - 7);
        g.lineTo(e.x + 6, y + 5);
        g.lineTo(e.x - 6, y + 5);
        g.closePath();
        g.fill();
        g.fillStyle = ENEMY_JET;
        g.fillRect(e.x - 2, y - 2, 4, 6);
      }
    }
  }

  private drawBullets(g: CanvasRenderingContext2D): void {
    for (const b of this.bullets) {
      if (!b.alive) continue;
      g.fillStyle = BULLET_GLOW;
      g.fillRect(b.x - 2, b.y - 5, 4, 10);
      g.fillStyle = BULLET;
      g.fillRect(b.x - 1, b.y - 4, 2, 8);
    }
  }

  private drawBooms(g: CanvasRenderingContext2D): void {
    for (const b of this.booms) {
      const r = 3 + b.t * 14;
      g.globalAlpha = 1 - b.t;
      g.fillStyle = b.t < 0.3 ? BOOM_CORE : b.t < 0.6 ? BOOM_HOT : BOOM_MID;
      g.beginPath();
      g.arc(b.x, b.y, r, 0, Math.PI * 2);
      g.fill();
    }
    g.globalAlpha = 1;
  }

  private drawShip(g: CanvasRenderingContext2D): void {
    const x = this.shipX;
    const y = SHIP_Y;
    // thruster
    g.fillStyle = SHIP_THRUST;
    const flame = 4 + (this.tick % 4);
    g.fillRect(x - 2, y + 6, 4, flame);
    // hull (arrowhead pointing up-river)
    g.fillStyle = SHIP_DARK;
    g.beginPath();
    g.moveTo(x, y - 10);
    g.lineTo(x + SHIP_HALF_W, y + 6);
    g.lineTo(x - SHIP_HALF_W, y + 6);
    g.closePath();
    g.fill();
    g.fillStyle = SHIP_BODY;
    g.beginPath();
    g.moveTo(x, y - 7);
    g.lineTo(x + 4, y + 4);
    g.lineTo(x - 4, y + 4);
    g.closePath();
    g.fill();
    g.fillStyle = SHIP_GLOW;
    g.fillRect(x - 1, y - 4, 2, 4);
  }

  // --------------------------------------------------------------------------
  // HUD + cards
  // --------------------------------------------------------------------------

  private drawHud(g: CanvasRenderingContext2D): void {
    g.font = '10px ui-monospace, monospace';
    g.textBaseline = 'top';

    g.textAlign = 'left';
    g.fillStyle = HUD_LABEL;
    g.fillText('SCORE', 8, 6);
    g.fillStyle = HUD_SCORE;
    g.fillText(String(Math.floor(this.score)).padStart(6, '0'), 8, 18);

    g.textAlign = 'center';
    g.fillStyle = HUD_LABEL;
    g.fillText('DIST', WIDTH / 2, 6);
    g.fillStyle = HUD_DIST;
    g.fillText(`${Math.floor(this.distanceM)}m`, WIDTH / 2, 18);

    // Lives as little ships, top-right.
    g.textAlign = 'right';
    g.fillStyle = HUD_LABEL;
    g.fillText('SHIPS', WIDTH - 8, 6);
    for (let i = 0; i < this.lives; i++) {
      const lx = WIDTH - 12 - i * 12;
      g.fillStyle = HUD_LIVES;
      g.beginPath();
      g.moveTo(lx, 18);
      g.lineTo(lx + 4, 26);
      g.lineTo(lx - 4, 26);
      g.closePath();
      g.fill();
    }

    this.drawFuelGauge(g);
    g.textAlign = 'left';
  }

  private drawFuelGauge(g: CanvasRenderingContext2D): void {
    // Vertical fuel gauge on the left edge — the eye's constant companion.
    const barW = 8;
    const barH = 90;
    const barX = 10;
    const barY = HEIGHT - barH - 16;
    const frac = this.fuel / FUEL_MAX;

    g.strokeStyle = FUEL_FRAME;
    g.lineWidth = 1;
    g.strokeRect(barX - 0.5, barY - 0.5, barW + 1, barH + 1);

    let fill: string;
    if (frac > 0.4) fill = FUEL_FULL;
    else if (frac > 0.18) fill = FUEL_WARN;
    else fill = FUEL_CRIT;
    // Flash when critical.
    const flashHide = frac <= 0.18 && Math.floor(this.tick / 6) % 2 === 0;
    if (!flashHide) {
      g.fillStyle = fill;
      const fh = Math.round(barH * frac);
      g.fillRect(barX, barY + (barH - fh), barW, fh);
    }

    g.font = '8px ui-monospace, monospace';
    g.textAlign = 'center';
    g.fillStyle = FUEL_FRAME;
    g.fillText('FUEL', barX + barW / 2, barY + barH + 3);
  }

  private drawCard(g: CanvasRenderingContext2D, title: string, sub: string, cta: string, ok = false): void {
    const w = 320;
    const h = 112;
    const x = (WIDTH - w) / 2;
    const y = (HEIGHT - h) / 2;
    g.fillStyle = CARD_BG;
    g.fillRect(x, y, w, h);
    g.strokeStyle = ok ? CARD_BORDER_OK : CARD_BORDER;
    g.lineWidth = 1;
    g.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);

    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillStyle = title === 'NEON RAPIDS' ? ATTRACT_TITLE : CARD_TEXT;
    g.font = 'bold 20px ui-monospace, monospace';
    g.fillText(title, WIDTH / 2, y + 32);

    g.fillStyle = CARD_SUB;
    g.font = '11px ui-monospace, monospace';
    g.fillText(sub, WIDTH / 2, y + 62);

    g.fillStyle = ATTRACT_PROMPT;
    g.font = '9px ui-monospace, monospace';
    if ((this.tick - this.phaseEnteredTick) % 40 < 28) {
      g.fillText(cta, WIDTH / 2, y + 88);
    }
    g.textAlign = 'left';
    g.textBaseline = 'top';
  }

  private drawReadyCard(g: CanvasRenderingContext2D): void {
    this.drawCard(
      g,
      'NEON RAPIDS',
      'fly the river · shoot · refuel over depots · mind the walls',
      'PRESS FIRE TO LAUNCH'
    );
  }

  private drawGameOverCard(g: CanvasRenderingContext2D): void {
    this.drawCard(
      g,
      'DOWN IN THE DELTA',
      `${Math.floor(this.distanceM)}m · SCORE ${this.finalScore}`,
      'PRESS FIRE TO FLY AGAIN'
    );
  }
}
